<template>
  <div class="modal-overlay" @click="close">
    <div class="modal-content" @click.stop>
      <button class="modal-close" @click="close">×</button>

      <div class="modal-header">
        <h2>Create Proposal</h2>
      </div>

      <div class="modal-body">
        <!-- Type Selection -->
        <div class="form-group">
          <label for="proposal-type">Proposal Type</label>
          <select v-model="formData.proposal_type" id="proposal-type" class="form-control">
            <option value="poll">Poll</option>
            <option value="budget">Budget</option>
            <option value="decision">Decision</option>
          </select>
        </div>

        <!-- Title -->
        <div class="form-group">
          <label for="title">Title</label>
          <input 
            v-model="formData.title" 
            id="title"
            type="text" 
            class="form-control"
            placeholder="Enter proposal title"
          />
        </div>

        <!-- Description -->
        <div class="form-group">
          <label for="description">Description</label>
          <textarea 
            v-model="formData.description" 
            id="description"
            class="form-control"
            rows="4"
            placeholder="Enter proposal description"
          ></textarea>
        </div>

        <!-- Type-Specific Fields -->
        <div v-if="formData.proposal_type === 'budget'" class="form-group">
          <label for="budget-amount">Budget Amount</label>
          <input 
            v-model="formData.data.amount" 
            id="budget-amount"
            type="number" 
            class="form-control"
            placeholder="Enter amount"
          />
        </div>

        <div v-if="formData.proposal_type === 'budget'" class="form-group">
          <label for="budget-purpose">Purpose</label>
          <input 
            v-model="formData.data.purpose" 
            id="budget-purpose"
            type="text" 
            class="form-control"
            placeholder="What is this budget for?"
          />
        </div>

        <div v-if="formData.proposal_type === 'budget'" class="form-group">
          <label for="budget-link">Link</label>
          <input 
            v-model="formData.data.link" 
            id="budget-link"
            type="url" 
            class="form-control"
            placeholder="https://example.com/details"
          />
        </div>


        <!-- Tags (Optional) -->
        <div class="form-group">
          <label for="tags">Tags (comma-separated)</label>
          <input 
            v-model="tagsInput" 
            id="tags"
            type="text" 
            class="form-control"
            placeholder="e.g., urgent, finance, infrastructure"
          />
        </div>

        <!-- Error -->
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" @click="close" :disabled="isSubmitting">Cancel</button>
        <button class="btn-primary" @click="submit" :disabled="isSubmitting">
          {{ isSubmitting ? 'Creating...' : 'Create Proposal' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'CreateProposal',
  emits: ['close', 'created'],
  setup(props, { emit }) {
    const store = useStore()
    const isSubmitting = ref(false)
    const error = ref('')
    const tagsInput = ref('')

    const formData = ref({
      proposal_type: 'poll',
      title: '',
      description: '',
      tags: [],
      data: {
        amount: '',
        purpose: '',
        link: ''
      }
    })

    const isValidUrl = (value) => {
      try {
        const u = new URL(value)
        return u.protocol === 'http:' || u.protocol === 'https:'
      } catch (_) {
        return false
      }
    }

    const validate = () => {
      error.value = ''

      const title = (formData.value.title || '').trim()
      if (title.length < 10) {
        error.value = 'Title must be at least 10 characters'
        return false
      }

      const description = (formData.value.description || '').trim()
      if (description.length < 20) {
        error.value = 'Description must be at least 20 characters'
        return false
      }

      if (formData.value.proposal_type === 'budget') {
        if (!formData.value.data.amount) {
          error.value = 'Budget amount is required'
          return false
        }
        if (!formData.value.data.purpose.trim()) {
          error.value = 'Budget purpose is required'
          return false
        }
        const link = (formData.value.data.link || '').trim()
        if (!link || !isValidUrl(link)) {
          error.value = 'A valid budget link is required'
          return false
        }
      }

      return true
    }

    const submit = async () => {
      if (!validate()) return

      isSubmitting.value = true
      error.value = ''

      try {
        // Parse tags
        formData.value.tags = tagsInput.value
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0)

        await store.dispatch('governance/createProposal', formData.value)
        emit('created')
      } catch (err) {
        error.value = err.message || 'Failed to create proposal'
      } finally {
        isSubmitting.value = false
      }
    }

    const close = () => {
      emit('close')
    }

    return {
      formData,
      tagsInput,
      isSubmitting,
      error,
      validate,
      submit,
      close
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #111827;
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-of-type {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #37352f;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  color: #37352f;
  background: white;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

textarea.form-control {
  font-family: inherit;
  resize: vertical;
}

.error-message {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  padding: 12px;
  color: #c00;
  font-size: 13px;
  margin-top: 16px;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-secondary,
.btn-primary {
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: #f3f4f6;
  color: #37352f;
  border: 1px solid #e9ecef;
}

.btn-secondary:hover:not(:disabled) {
  background: #e9ecef;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

</style>
